import type { ReportNodeType, SelectedReportType } from "./types";
import type { TranslateWrapperProps } from "interfaces/shared.types";
import type { Translate } from "hooks/useTranslate";

export const findItemById = (
  items: ReportNodeType[],
  targetId: string,
): ReportNodeType | null => {
  for (const item of items) {
    if (item.id === targetId) {
      return item;
    }
    if (item.children) {
      const found = findItemById(item.children, targetId);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

export const updateItemSelection = (
  items: ReportNodeType[],
  targetId: string | "all",
  selected: boolean,
  handleChangeSelectedCount: (_newCount: number) => void,
): ReportNodeType[] => {
  let totalChangeCount = 0;

  const updatedItems = items.map((item) => {
    if (item.id === targetId || targetId === "all") {
      const updateChildren = (
        children?: ReportNodeType[],
      ): ReportNodeType[] | undefined => {
        if (!children) {
          return undefined;
        }

        return children.map((child) => {
          if (!child.reportItemsWrapper && child.selected !== selected) {
            totalChangeCount += selected ? 1 : -1;
          }

          return {
            ...child,
            selected,
            children: updateChildren(child.children),
          };
        });
      };

      if (!item.reportItemsWrapper && item.selected !== selected) {
        totalChangeCount += selected ? 1 : -1;
      }

      return {
        ...item,
        selected,
        children: updateChildren(item.children),
      };
    }

    if (item.children) {
      const updatedChildren = updateItemSelection(
        item.children,
        targetId,
        selected,
        handleChangeSelectedCount,
      );

      const anyChildSelected = updatedChildren.some((child) => child.selected);

      if (!item.reportItemsWrapper && item.selected !== anyChildSelected) {
        totalChangeCount += anyChildSelected ? 1 : -1;
      }

      return {
        ...item,
        selected: anyChildSelected,
        children: updatedChildren,
      };
    }

    return item;
  });

  if (totalChangeCount !== 0) {
    handleChangeSelectedCount(totalChangeCount);
  }

  return updatedItems;
};

export const getSelectedReportIds = (
  items: ReportNodeType[],
  translate: Translate,
): SelectedReportType[] => {
  const selectedIds: SelectedReportType[] = [];

  const translateNode = (node: ReportNodeType): string => {
    const translated = translate(node.translationKey);
    if (typeof translated === "string") {
      return translated;
    }
    return (translated.props as TranslateWrapperProps).text as string;
  };

  const traverse = (
    nodes: ReportNodeType[],
    parent: ReportNodeType | null = null,
  ) => {
    nodes.forEach((node) => {
      if (node.selected && !node.reportItemsWrapper) {
        selectedIds.push({
          type: node.id,
          fileName: translateNode(node),
          directoryName: translateNode(parent || node),
        });
      }

      if (node.children && node.children.length > 0) {
        traverse(node.children, node);
      }
    });
  };
  traverse(items);

  return selectedIds;
};

export const areItemsEqual = (
  prev: ReportNodeType,
  next: ReportNodeType,
): boolean =>
  prev.id === next.id &&
  prev.selected === next.selected &&
  prev.translationKey === next.translationKey &&
  JSON.stringify(prev.children) === JSON.stringify(next.children);

export const calculateInitialCounters = (
  items: ReportNodeType[],
): {
  checkBoxesCount: number;
  checkBoxesSelected: number;
} => {
  let checkBoxesSelected = 0;
  let checkBoxesCount = 0;

  const traverse = (nodes: ReportNodeType[]) => {
    nodes.forEach((node) => {
      if (!node.reportItemsWrapper) {
        checkBoxesCount += 1;
        if (node.selected) {
          checkBoxesSelected += 1;
        }
      }

      if (node.children && node.children.length > 0) {
        traverse(node.children);
      }
    });
  };

  traverse(items);
  return { checkBoxesCount, checkBoxesSelected };
};
